import { SupportedLocale, TranslationSchema, SUPPORTED_LOCALES } from './types';
import { TRANSLATIONS } from './translations';

const STORAGE_KEY = 'purin_locale';

export function detectInitialLocale(): SupportedLocale {
  // 1. URLクエリパラメータ (?lang=ja, ?lang=en など) を最優先
  if (typeof window !== 'undefined' && window.location) {
    try {
      const params = new URLSearchParams(window.location.search);
      const param = params.get('lang') as SupportedLocale | null;
      if (param && SUPPORTED_LOCALES.some((l) => l.code === param)) {
        return param;
      }
    } catch {}
  }

  // 2. 保存済みのローカルストレージ設定
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as SupportedLocale | null;
      if (saved && SUPPORTED_LOCALES.some((l) => l.code === saved)) {
        return saved;
      }
    } catch {
      // localStorage may fail in private mode or if disabled
    }
  }

  // デフォルト言語: English (英語)
  return 'en';
}

export class I18nService {
  private static instance: I18nService | null = null;
  private currentLocale: SupportedLocale;
  private listeners: Set<(locale: SupportedLocale) => void> = new Set();

  constructor(initialLocale?: SupportedLocale) {
    this.currentLocale = initialLocale || detectInitialLocale();
  }

  static getInstance(): I18nService {
    if (!I18nService.instance) {
      I18nService.instance = new I18nService();
    }
    return I18nService.instance;
  }

  getLocale(): SupportedLocale {
    return this.currentLocale;
  }

  setLocale(locale: SupportedLocale): void {
    if (this.currentLocale === locale) return;
    this.currentLocale = locale;

    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEY, locale);
      } catch {
        // ignore
      }
    }

    this.notify();
  }

  t(): TranslationSchema {
    return TRANSLATIONS[this.currentLocale] || TRANSLATIONS.en;
  }

  getTranslation(locale: SupportedLocale): TranslationSchema {
    return TRANSLATIONS[locale] || TRANSLATIONS.en;
  }

  subscribe(listener: (locale: SupportedLocale) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.currentLocale);
      } catch (err) {
        console.error('[I18nService] Listener error:', err);
      }
    });
  }
}

export const i18n = I18nService.getInstance();
export const t = (): TranslationSchema => i18n.t();
export const subscribeLocaleChange = (listener: (locale: SupportedLocale) => void): (() => void) => i18n.subscribe(listener);
export const setLocale = (locale: SupportedLocale): void => i18n.setLocale(locale);

